import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { StaffOrderDetail } from "@/api/staff";
import { useOrderCommonFun } from "../../hooks/useOrderCommonFun";
import { submitOrderFinish, submitPunchCard } from "@/api/staff";
import { uploadImage } from "@/api/upload";
import { toastSuccess, toastWarning } from "@/utils/toast";

interface Props {
  orderInfo: StaffOrderDetail;
  onUpdate?: () => void;
}

export function DetailBtnGroup({ orderInfo, onUpdate }: Props) {
  const navigate = useNavigate();
  const common = useOrderCommonWithUrl(orderInfo);
  const [showPouchCard, setShowPouchCard] = useState(false);
  const [pouchContent, setPouchContent] = useState("");
  const [pouchImgs, setPouchImgs] = useState<string[]>([]);
  const [pouchSubmitting, setPouchSubmitting] = useState(false);
  const [pouchUploading, setPouchUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // status=20 => 到达地点打卡；status=30 => 完成服务打卡（并完成服务）
  const pouchMode = useMemo<"arrive" | "finish">(() => (orderInfo.status === 30 ? "finish" : "arrive"), [orderInfo.status]);

  const handleStaffDepart = () => {
    const id = (orderInfo as any)?.id ?? common.orderId;
    navigate(`/workspace/order/punch-depart?id=${id}`);
  };
  // 到达：与出发一样跳转打卡地图页（带 type=arrive 区分）
  const handleStaffArriveNav = () => {
    const id = (orderInfo as any)?.id ?? common.orderId;
    navigate(`/workspace/order/punch-depart?id=${id}&type=arrive`);
  };
  const handleArriveClick = () => common.handleStaffArrive(onUpdate);
  // 开始服务 → 跳转打卡地图页（type=start）
  const handleOrderStart = () => {
    const id = (orderInfo as any)?.id ?? common.orderId;
    navigate(`/workspace/order/punch-depart?id=${id}&type=start`);
  };
  // 完成服务 → 跳转打卡地图页（type=finish）
  const handleOrderFinish = () => {
    const id = (orderInfo as any)?.id ?? common.orderId;
    navigate(`/workspace/order/punch-depart?id=${id}&type=finish`);
  };
  const handleClock = () => {
    common.handleStaffLocationVerify(() => {
      // 到达地点：地理校验成功后进入打卡
      setPouchContent("");
      setPouchImgs([]);
      setShowPouchCard(true);
    });
  };

  const onPickFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    if (pouchImgs.length >= 4) return toastWarning("最多上传4张图片");
    const picked = Array.from(files).slice(0, 4 - pouchImgs.length);
    setPouchUploading(true);
    try {
      const uploaded = await Promise.all(picked.map((f) => uploadImage(f)));
      setPouchImgs((p) => [...p, ...uploaded].slice(0, 4));
    } finally {
      setPouchUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImg = (idx: number) => setPouchImgs((p) => p.filter((_, i) => i !== idx));

  const submitPouchCard = async () => {
    const orderId = (orderInfo as any)?.id ?? common.orderId;
    if (!orderId) return toastWarning("缺少订单ID");
    if (!pouchContent.trim()) return toastWarning("请输入备注信息");
    if (pouchMode === "finish" && pouchImgs.length === 0) return toastWarning("请上传打卡图片");
    if (pouchSubmitting) return;

    setPouchSubmitting(true);
    try {
      if (pouchMode === "arrive") {
        // 到达地点提交打卡（严格：已在打开弹窗前做过 punch_card_verify）
        await submitPunchCard({
          order_id: Number(orderId),
          content: pouchContent.trim(),
          imgs: pouchImgs,
        });
      } else {
        // 服务完成提交打卡并完成服务（严格：已在打开弹窗前做过 finish_verify + punch_card_verify）
        await submitOrderFinish({
          order_id: Number(orderId),
          content: pouchContent.trim(),
          imgs: pouchImgs,
        });
      }
      toastSuccess("打卡成功");
      setShowPouchCard(false);
      onUpdate?.();
    } finally {
      setPouchSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex items-center gap-2">
      {/* 签约完成(status>=16) + 未出发(is_depart<1) → 显示出发按钮 */}
      {orderInfo.status >= 16 && (orderInfo.is_depart ?? 0) < 1 && (
        <button onClick={handleStaffDepart} className="flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-full text-sm font-semibold active:scale-[0.98]">
          服务人员出发
        </button>
      )}

      {/* 已出发未到达(is_depart=1) + 签约完成(status>=16) → 显示到达按钮 */}
      {orderInfo.status >= 16 && (orderInfo.is_depart ?? 0) === 1 && (
        <button onClick={handleStaffArriveNav} className="flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-full text-sm font-semibold active:scale-[0.98]">
          服务人员到达
        </button>
      )}

      {/* 待服务及之后(status>=20) + is_depart>=2 → 后续操作按钮（打卡/开始服务/完成/评价） */}
      {orderInfo.status >= 20 && (orderInfo.is_depart ?? 0) >= 2 && (
        <>
          {orderInfo.status < 40 && (
            <button
              type="button"
              onClick={() => {
                const id = (orderInfo as any)?.id ?? common.orderId;
                navigate(`/workspace/order/report?id=${id}`);
              }}
              className="flex-1 py-2.5 bg-gray-200 rounded-full text-sm font-medium active:scale-[0.98]"
            >
              报备
            </button>
          )}
          {orderInfo.is_depart === 1 && (
            <button onClick={handleArriveClick} className="flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-full text-sm font-semibold active:scale-[0.98]">服务人员到达</button>
          )}
          {orderInfo.is_depart === 2 && (
            <button onClick={handleClock} className="flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-full text-sm font-semibold active:scale-[0.98]">去打卡</button>
          )}
          {orderInfo.is_depart === 3 && orderInfo.status === 20 && (
            <button onClick={handleOrderStart} className="flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-full text-sm font-semibold active:scale-[0.98]">开始服务</button>
          )}
          {orderInfo.status === 30 && (
            <button onClick={handleOrderFinish} className="flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-full text-sm font-semibold active:scale-[0.98]">完成服务</button>
          )}
        </>
      )}

      {/* 待评价 → 去评价 */}
      {orderInfo.status === 40 && Number((orderInfo as any).is_staff_comment ?? 0) === 0 && (
        <button
          onClick={() => {
            const id = (orderInfo as any)?.id ?? common.orderId;
            navigate(`/workspace/order/comment?id=${id}`, {
              state: {
                // uni-app：userInfo 表示客户信息（头像/昵称等）
                userInfo: (orderInfo as any)?.userInfo ?? null,
              },
            });
          }}
          className="flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-full text-sm font-semibold"
        >
          去评价
        </button>
      )}

      {/* 打卡弹窗（简化版：仅提示） */}
      {showPouchCard && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setShowPouchCard(false)}>
          <div className="bg-white w-full max-w-md mx-auto rounded-t-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-bold mb-4 text-center">{pouchMode === "finish" ? "完成服务打卡" : "到达地点打卡"}</p>

            <textarea
              value={pouchContent}
              onChange={(e) => setPouchContent(e.target.value)}
              placeholder="备注信息"
              className="w-full border border-gray-200 rounded-xl p-3 mb-4 text-sm resize-none outline-none"
              rows={4}
            />

            {/* 图片上传 — 仅完成服务模式时显示 */}
            {pouchMode === "finish" && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {pouchImgs.map((u, idx) => (
                  <div key={`${u}-${idx}`} className="relative w-[70px] h-[70px]">
                    <img src={u} alt="" className="w-full h-full rounded-2xl object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImg(idx)}
                      className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center active:scale-[0.98]"
                      aria-label="删除图片"
                    >
                      <i className="ri-close-line text-gray-500" />
                    </button>
                  </div>
                ))}

                {pouchImgs.length < 4 && (
                  <button
                    type="button"
                    disabled={pouchUploading}
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-[70px] h-[70px] rounded-2xl bg-[#f8f8f8] flex items-center justify-center active:scale-[0.98] ${pouchUploading ? "opacity-60" : ""}`}
                    aria-label="上传图片"
                  >
                    <i className="ri-add-line text-2xl text-gray-400" />
                  </button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => onPickFiles(e.target.files)}
              />
            </div>
            )}

            <div className="flex gap-3">
              <button onClick={() => setShowPouchCard(false)} className="flex-1 py-2.5 bg-gray-100 rounded-xl text-sm">取消</button>
              <button
                type="button"
                onClick={submitPouchCard}
                disabled={pouchSubmitting}
                className={`flex-1 py-2.5 text-white bg-[#FE7A3A] rounded-xl text-sm font-medium active:scale-[0.98] ${pouchSubmitting ? "opacity-60" : ""}`}
              >
                {pouchSubmitting ? "提交中..." : "提交打卡"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** 内部封装 useOrderCommonFun，自动注入 orderId */
function useOrderCommonWithUrl(orderInfo: StaffOrderDetail) {
  // orderId 从上下文传入或从 URL 获取
  const searchParams = new URLSearchParams(window.location.search);
  const orderId = Number(searchParams.get("id") || "0");
  const common = useOrderCommonFun(orderId, {
    latitude: (orderInfo as any)?.latitude,
    longitude: (orderInfo as any)?.longitude,
  });
  return { ...common, orderId };
}
